"use client";

import { c as _c } from "react/compiler-runtime";
import { IconMagnifyingGlass } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconArrowLeftOutline18, IconArrowRightOutline18, IconChatBubbleOutline18, IconCopyOutline18, IconDotsOutline18, IconTrashOutline18 } from "nucleo-ui-outline-18";
import { getPrimaryErrorMessage } from "@/components/errors/UnifiedErrorPanel";
import { CaddieAvatar } from "@/components/caddie/CaddieAvatar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ResolvedDescriptionContent } from "@/components/ui/workflow-step-row/components/ResolvedDescriptionContent";
import { type InlineTokenData } from "@/components/ui/workflow-step-row/components/InlineAddressLink";
import { useAIChatStore } from "@/stores/aiChatStore";
import { resolveDescriptionTemplates, type DescriptionFormatHints } from "@/utils/resolveDescriptionTemplates";
import { NodeToolbar } from "@xyflow/react";
import { useEffect, useRef, useState, type RefObject } from "react";
import { useNodeToolbarPlacement } from "./useNodeToolbarPlacement";
interface NodeDescriptionToolbarProps {
  description?: string;
  errorMessage?: string;
  /** The node's default description from the definition - if it matches description, hide the toolbar */
  defaultDescription?: string;
  isVisible: boolean;
  /** Node name for AI context */
  nodeName?: string;
  /** Node ID for AI context */
  nodeId?: string;
  onInspect?: () => void;
  onComment?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  /** Token data for inline display - addresses matching these will show as token badges */
  tokens?: InlineTokenData[];
  /** Callback when hover state changes - used to keep toolbar visible while interacting */
  onHoverChange?: (isHovered: boolean) => void;
  /** Ref to the rendered node element, used to avoid header overlap. */
  nodeRef?: RefObject<HTMLDivElement | null>;
  /** Node payload for resolving {payload.field} template variables in descriptions */
  payload?: Record<string, unknown>;
  /** Format hints for smart value display (token decimals, symbol, etc.) */
  formatHints?: DescriptionFormatHints;
}

/**
 * NodeDescriptionToolbar - Shows AI-generated description above a node
 * Displayed on hover or when the node is selected
 * Includes action buttons: Ask AI, Comment, More Actions (Duplicate, Delete)
 */
export function NodeDescriptionToolbar(t0) {
  const $ = _c(61);
  const {
    description,
    errorMessage,
    defaultDescription,
    isVisible,
    nodeName,
    nodeId,
    onInspect,
    onComment,
    onDuplicate,
    onDelete,
    tokens,
    onHoverChange,
    nodeRef,
    payload,
    formatHints
  } = t0;
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isAskingAI, setIsAskingAI] = useState(false);
  const [aiMessage, setAiMessage] = useState("");
  const inputRef = useRef(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  let t1;
  if ($[0] !== nodeRef || $[1] !== shouldRender) {
    t1 = {
      isVisible: shouldRender,
      nodeRef,
      offset: 8
    };
    $[0] = nodeRef;
    $[1] = shouldRender;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    bridgeClassName,
    enterAnimationClassName,
    exitAnimationClassName,
    position,
    toolbarRef
  } = useNodeToolbarPlacement(t1);
  let t2;
  if ($[3] !== isVisible || $[4] !== shouldRender) {
    t2 = () => {
      if (isVisible) {
        setShouldRender(true);
        setIsAnimatingOut(false);
      } else {
        if (shouldRender) {
          setIsAnimatingOut(true);
          const timeout = setTimeout(() => {
            setShouldRender(false);
            setIsAnimatingOut(false);
          }, 200);
          return () => clearTimeout(timeout);
        }
      }
    };
    $[3] = isVisible;
    $[4] = shouldRender;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== isVisible) {
    t3 = [isVisible];
    $[6] = isVisible;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  const openAIPanel = useAIChatStore(_temp);
  const setPendingMessage = useAIChatStore(_temp2);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsAskingAI(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleAskAIClick = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setIsAskingAI(false);
      setAiMessage("");
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleBackClick = t5;
  let t6;
  if ($[10] !== aiMessage || $[11] !== nodeId || $[12] !== nodeName || $[13] !== openAIPanel || $[14] !== setPendingMessage) {
    t6 = () => {
      if (!aiMessage.trim()) {
        return;
      }
      const contextPrefix = nodeName ? `In my ${nodeName}${nodeId ? ` (ID: ${nodeId})` : ""}: ` : "";
      const fullMessage = contextPrefix + aiMessage.trim();
      setPendingMessage(fullMessage);
      openAIPanel();
      setAiMessage("");
      setIsAskingAI(false);
    };
    $[10] = aiMessage;
    $[11] = nodeId;
    $[12] = nodeName;
    $[13] = openAIPanel;
    $[14] = setPendingMessage;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleSendMessage = t6;
  let t7;
  if ($[16] !== handleSendMessage) {
    t7 = e => {
      e.stopPropagation();
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      } else {
        if (e.key === "Escape") {
          handleBackClick();
        }
      }
    };
    $[16] = handleSendMessage;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleKeyDown = t7;
  let displayedText;
  let errorSummary;
  let isErrorState;
  let t8;
  if ($[18] !== defaultDescription || $[19] !== description || $[20] !== errorMessage || $[21] !== formatHints || $[22] !== payload) {
    const resolvedDescription = resolveDescriptionTemplates(description, payload, formatHints);
    errorSummary = errorMessage ? getPrimaryErrorMessage(errorMessage) : null;
    isErrorState = !!errorSummary;
    displayedText = errorSummary ?? resolvedDescription;
    const normalize = _temp3;
    t8 = !displayedText || !isErrorState && normalize(displayedText) === normalize(defaultDescription);
    $[18] = defaultDescription;
    $[19] = description;
    $[20] = errorMessage;
    $[21] = formatHints;
    $[22] = payload;
    $[23] = displayedText;
    $[24] = errorSummary;
    $[25] = isErrorState;
    $[26] = t8;
  } else {
    displayedText = $[23];
    errorSummary = $[24];
    isErrorState = $[25];
    t8 = $[26];
  }
  if (t8) {
    return null;
  }
  if (!shouldRender) {
    return null;
  }
  const t9 = `relative w-full max-w-[400px] rounded-xl border p-4 pb-3 shadow-lg backdrop-blur-md duration-200 ${isErrorState ? "shadow-ds-error-glow border-red-200 bg-white/95 dark:border-red-900/60 dark:bg-zinc-900/95" : "border-ds-border bg-white/90 dark:bg-zinc-900/90"} ${isAnimatingOut ? `animate-out fade-out ${exitAnimationClassName}` : `animate-in fade-in ${enterAnimationClassName}`}`;
  let t10;
  let t11;
  if ($[27] !== onHoverChange) {
    t10 = () => onHoverChange?.(true);
    t11 = () => onHoverChange?.(false);
    $[27] = onHoverChange;
    $[28] = t10;
    $[29] = t11;
  } else {
    t10 = $[28];
    t11 = $[29];
  }
  let t12;
  if ($[30] !== bridgeClassName) {
    t12 = <div className={bridgeClassName} />;
    $[30] = bridgeClassName;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== aiMessage || $[33] !== description || $[34] !== displayedText || $[35] !== dropdownOpen || $[36] !== errorSummary || $[37] !== formatHints || $[38] !== handleKeyDown || $[39] !== handleSendMessage || $[40] !== isAskingAI || $[41] !== isErrorState || $[42] !== nodeName || $[43] !== onComment || $[44] !== onDelete || $[45] !== onDuplicate || $[46] !== onInspect || $[47] !== payload || $[48] !== tokens) {
    t13 = isAskingAI ? <div className="flex flex-col gap-2"><Input ref={inputRef} type="text" value={aiMessage} onChange={e_0 => {
        e_0.stopPropagation();
        setAiMessage(e_0.target.value);
      }} onKeyDown={handleKeyDown} onClick={_temp4} placeholder="Ask Caddie about this node..." className="text-ds-text-primary w-[320px] border-0 bg-transparent p-1" /><div className="border-ds-border-default flex items-center justify-between border-t pt-2"><button onClick={e_2 => {
          e_2.stopPropagation();
          handleBackClick();
        }} className="text-ds-text-muted hover:bg-ds-surface-hover hover:text-ds-text-secondary dark:hover:bg-ds-neutral-700 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors dark:hover:text-zinc-200"><IconArrowLeftOutline18 className="h-3.5 w-3.5" />Back</button><Button onClick={e_3 => {
          e_3.stopPropagation();
          handleSendMessage();
        }} disabled={!aiMessage.trim()}><span className="text-xs">Ask</span> <IconArrowRightOutline18 className="ml-1.5 size-3.5" /></Button></div></div> : <>{displayedText && <div>{isErrorState && <p className="label-xs-caps mb-2 text-red-500 dark:text-red-400">Error</p>}<div className={`text-xs leading-relaxed ${isErrorState ? "text-red-700 dark:text-red-300" : "text-ds-text-secondary"}`}>{isErrorState ? <p className="text-wrap-pretty">{errorSummary}</p> : <ResolvedDescriptionContent text={description} payload={payload} formatHints={formatHints} tokens={tokens} />}</div></div>}<div className={`flex items-center justify-between ${displayedText ? `mt-3 border-t pt-2 ${isErrorState ? "border-red-200/80 dark:border-red-900/50" : "border-ds-neutral-200/70 dark:border-ds-neutral-700"}` : ""}`}><div className="flex items-center gap-1">{nodeName && <button onClick={e_4 => {
            e_4.stopPropagation();
            handleAskAIClick();
          }} className="dark:hover:bg-ds-neutral-700 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium uppercase tracking-wider text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 dark:text-blue-400 dark:hover:text-zinc-200"><CaddieAvatar size={20} mood="conversation" />Ask</button>}{onInspect && <button onClick={e_5 => {
            e_5.stopPropagation();
            onInspect();
          }} className="text-ds-text-subtle hover:bg-ds-surface-hover hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium uppercase tracking-wider transition-colors dark:hover:text-zinc-200"><IconMagnifyingGlass className="h-3.5 w-3.5" />Inspect</button>}{onComment && <button onClick={e_6 => {
            e_6.stopPropagation();
            onComment();
          }} className="text-ds-text-subtle hover:bg-ds-surface-hover hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors dark:hover:text-zinc-200"><IconChatBubbleOutline18 className="h-3.5 w-3.5" /></button>}</div>{(onDuplicate || onDelete) && <Popover open={dropdownOpen} onOpenChange={setDropdownOpen}><PopoverTrigger asChild={true}><button onClick={_temp5} className="text-ds-text-subtle hover:bg-ds-surface-hover hover:text-ds-text-primary dark:text-ds-text-disabled dark:hover:bg-ds-neutral-700 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium transition-colors dark:hover:text-zinc-200"><IconDotsOutline18 className="h-3.5 w-3.5" /></button></PopoverTrigger><PopoverContent align="end" className="bg-ds-surface-card w-36 p-1">{onDuplicate && <button onClick={e_8 => {
              e_8.stopPropagation();
              onDuplicate();
              setDropdownOpen(false);
            }} className="text-ds-text-secondary hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-700 flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs uppercase tracking-wider transition-colors"><IconCopyOutline18 className="h-3.5 w-3.5" />Duplicate</button>}{onDelete && <button onClick={e_9 => {
              e_9.stopPropagation();
              onDelete();
              setDropdownOpen(false);
            }} className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs uppercase tracking-wider text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"><IconTrashOutline18 className="h-3.5 w-3.5" />Delete</button>}</PopoverContent></Popover>}</div></>;
    $[32] = aiMessage;
    $[33] = description;
    $[34] = displayedText;
    $[35] = dropdownOpen;
    $[36] = errorSummary;
    $[37] = formatHints;
    $[38] = handleKeyDown;
    $[39] = handleSendMessage;
    $[40] = isAskingAI;
    $[41] = isErrorState;
    $[42] = nodeName;
    $[43] = onComment;
    $[44] = onDelete;
    $[45] = onDuplicate;
    $[46] = onInspect;
    $[47] = payload;
    $[48] = tokens;
    $[49] = t13;
  } else {
    t13 = $[49];
  }
  let t14;
  if ($[50] !== t10 || $[51] !== t11 || $[52] !== t12 || $[53] !== t13 || $[54] !== t9 || $[55] !== toolbarRef) {
    t14 = <div ref={toolbarRef} className={t9} onMouseEnter={t10} onMouseLeave={t11}>{t12}{t13}</div>;
    $[50] = t10;
    $[51] = t11;
    $[52] = t12;
    $[53] = t13;
    $[54] = t9;
    $[55] = toolbarRef;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== position || $[58] !== shouldRender || $[59] !== t14) {
    t15 = <NodeToolbar isVisible={shouldRender} position={position} align="center" offset={8}>{t14}</NodeToolbar>;
    $[57] = position;
    $[58] = shouldRender;
    $[59] = t14;
    $[60] = t15;
  } else {
    t15 = $[60];
  }
  return t15;
}
function _temp5(e_7) {
  return e_7.stopPropagation();
}
function _temp4(e_1) {
  return e_1.stopPropagation();
}
function _temp3(s) {
  return s?.trim().replace(/\s+/g, " ").toLowerCase().slice(0, 100);
}
function _temp2(state_0) {
  return state_0.setPendingMessage;
}
function _temp(state) {
  return state.openPanel;
}